import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import type { Job } from "@/utils/types";

/**
 * Props for the JobResultDisplay component.
 */
interface JobResultDisplayProps {
  /** The job whose result to display */
  job: Job;
}

/**
 * A stat card for displaying a single metric.
 */
function StatCard({
  label,
  value,
  variant = "default",
}: {
  label: string;
  value: number | string;
  variant?: "default" | "success" | "warning" | "error";
}) {
  const variantClasses = {
    default: "bg-muted",
    success: "bg-green-100 dark:bg-green-900/20",
    warning: "bg-amber-100 dark:bg-amber-900/20",
    error: "bg-red-100 dark:bg-red-900/20",
  };

  return (
    <div className={`rounded-lg p-3 ${variantClasses[variant]}`}>
      <div className="text-2xl font-bold">{value}</div>
      <div className="text-sm text-muted-foreground">{label}</div>
    </div>
  );
}

/**
 * Display for content_update job results.
 */
function ContentUpdateResult({ result }: { result: Record<string, unknown> }) {
  const total = (result.total as number) ?? (result.checked as number) ?? 0;
  const updated = (result.updated as number) ?? (result.updated_count as number) ?? 0;
  const failed = (result.failed as number) ?? 0;

  return (
    <div className="grid grid-cols-3 gap-3">
      <StatCard label="Documents Checked" value={total} />
      <StatCard label="Updated" value={updated} variant={updated > 0 ? "success" : "default"} />
      <StatCard label="Failed" value={failed} variant={failed > 0 ? "error" : "default"} />
    </div>
  );
}

/**
 * Display for quality_check job results.
 */
function QualityCheckResult({ result }: { result: Record<string, unknown> }) {
  const high = (result.high_priority as number) ?? 0;
  const medium = (result.medium_priority as number) ?? 0;
  const low = (result.low_priority as number) ?? 0;
  const totalIssues = high + medium + low;

  if (totalIssues === 0) {
    return (
      <div className="text-center py-4">
        <div className="text-green-600 dark:text-green-400 font-medium">No issues found</div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-3 gap-3">
      <StatCard label="High Priority" value={high} variant={high > 0 ? "error" : "default"} />
      <StatCard
        label="Medium Priority"
        value={medium}
        variant={medium > 0 ? "warning" : "default"}
      />
      <StatCard label="Low Priority" value={low} />
    </div>
  );
}

/**
 * Display for crawl_documentation job results.
 */
function CrawlDocumentationResult({ result }: { result: Record<string, unknown> }) {
  const discovered = (result.total_pages_discovered as number) ?? 0;
  const relevant = (result.relevant_pages as number) ?? 0;
  const excluded = (result.excluded_pages as number) ?? 0;

  return (
    <div className="grid grid-cols-3 gap-3">
      <StatCard label="Pages Discovered" value={discovered} />
      <StatCard label="Relevant Pages" value={relevant} variant="success" />
      <StatCard label="Excluded" value={excluded} />
    </div>
  );
}

/**
 * Display for cleanup job results.
 */
function CleanupResult({ result }: { result: Record<string, unknown> }) {
  const deleted = (result.deleted as number) ?? 0;

  return (
    <div className="grid grid-cols-1 gap-3 max-w-xs">
      <StatCard label="Items Deleted" value={deleted} />
    </div>
  );
}

/**
 * Display for auto_fix job results.
 */
function AutoFixResult({ result }: { result: Record<string, unknown> }) {
  const fixed = (result.fixed as number) ?? 0;

  return (
    <div className="grid grid-cols-1 gap-3 max-w-xs">
      <StatCard label="Issues Fixed" value={fixed} variant={fixed > 0 ? "success" : "default"} />
    </div>
  );
}

/**
 * Display for export job results.
 */
function ExportResult({ result }: { result: Record<string, unknown> }) {
  const exported = (result.documents_exported as number) ?? 0;

  return (
    <div className="grid grid-cols-1 gap-3 max-w-xs">
      <StatCard label="Documents Exported" value={exported} variant="success" />
    </div>
  );
}

/**
 * Fallback display for unknown job types.
 */
function GenericResult({ result }: { result: Record<string, unknown> }) {
  // Extract any numeric values to display
  const numericEntries = Object.entries(result).filter(([, value]) => typeof value === "number");

  if (numericEntries.length === 0) {
    return <div className="text-muted-foreground">Job completed successfully</div>;
  }

  return (
    <div className="grid grid-cols-3 gap-3">
      {numericEntries.slice(0, 6).map(([key, value]) => (
        <StatCard
          key={key}
          label={key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
          value={value as number}
        />
      ))}
    </div>
  );
}

/**
 * A structured display component for job results.
 * Shows job-type-specific formatted output with a collapsible raw JSON section.
 *
 * @example
 * ```tsx
 * <JobResultDisplay job={job} />
 * ```
 */
export function JobResultDisplay({ job }: JobResultDisplayProps) {
  const result = job.result as Record<string, unknown> | null;

  // Handle null or empty result
  if (!result || Object.keys(result).length === 0) {
    return (
      <div className="text-muted-foreground" data-testid="job-result-display">
        No results available
      </div>
    );
  }

  // Render job-type-specific display
  const renderResultContent = () => {
    switch (job.job_type) {
      case "content_update":
        return <ContentUpdateResult result={result} />;
      case "quality_check":
        return <QualityCheckResult result={result} />;
      case "crawl_documentation":
        return <CrawlDocumentationResult result={result} />;
      case "cleanup":
        return <CleanupResult result={result} />;
      case "auto_fix":
        return <AutoFixResult result={result} />;
      case "export":
        return <ExportResult result={result} />;
      default:
        return <GenericResult result={result} />;
    }
  };

  return (
    <div className="space-y-4" data-testid="job-result-display">
      {/* Structured result display */}
      {renderResultContent()}

      {/* Collapsible raw JSON section */}
      <Accordion type="single" collapsible className="w-full">
        <AccordionItem value="raw-result" className="border-none">
          <AccordionTrigger className="text-sm text-muted-foreground hover:no-underline py-2">
            Show Raw Result
          </AccordionTrigger>
          <AccordionContent>
            <pre
              className="text-xs bg-muted p-3 rounded-md overflow-x-auto"
              data-testid="job-result-raw"
            >
              {JSON.stringify(result, null, 2)}
            </pre>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}
